// "Minhas delegações" (`/me/delegations`; Parte 5.7/14; REQ-AUTHZ-008; GIY-075): as delegações da própria pessoa que o investigador principal já assinou — as que aguardam o
// seu aceite (com a ação de aceitar, step-up, hash e o significado "Aceito as tarefas delegadas") e as demais (vigentes, suspensas, agendadas e encerradas, com o motivo).
// Nunca lista a delegação de outra pessoa (o servidor responde 404). Sem escopo de tenant: o aceite troca o escopo no servidor e o `/me` é recarregado ao concluir.
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@giyus/ui';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { AccountMeNav } from '../../../pages/Account';
import { useCivilDate } from '../../people/lib/civilDateDisplay';
import { type MyDelegation, useMyDelegations } from '../api/delegations';
import { AcceptDelegationDialog } from './components/AcceptDelegationDialog';
import { DelegationReasonText, DelegationSignatures } from './components/DelegationLogTable';
import { DelegationStatusBadge } from './components/DelegationStatusBadge';
import { shortId } from './delegationModel';
import { useFunctionName, useTaskName } from './delegationNames';

export function MyDelegationsPage() {
  const { t } = useTranslation('authz');
  const mine = useMyDelegations();
  const [accepting, setAccepting] = useState<MyDelegation | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const items = mine.data ?? [];
  const pending = items.filter((item) => item.delegation.status === 'PendingAcceptance');
  const others = items.filter((item) => item.delegation.status !== 'PendingAcceptance');

  return (
    <section className="mx-auto flex max-w-5xl flex-col gap-6" data-testid="my-delegations">
      <div>
        <h1 className="text-headline font-semibold tracking-tight">{t('me.delegations.title')}</h1>
        <p className="text-muted-foreground">{t('me.delegations.subtitle')}</p>
      </div>
      <AccountMeNav />
      {message && (
        <p role="status" className="text-body" data-testid="my-delegations-message">
          {message}
        </p>
      )}
      {mine.isError ? (
        <ProblemAlert
          error={mine.error}
          onRetry={() => {
            void mine.refetch();
          }}
        />
      ) : mine.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <section className="flex flex-col gap-2" aria-labelledby="my-delegations-pending-title">
            <h2 id="my-delegations-pending-title" className="text-title font-semibold">
              {t('me.delegations.pending.title')}
            </h2>
            {pending.length === 0 ? (
              <p
                className="text-body text-muted-foreground"
                data-testid="my-delegations-pending-empty"
              >
                {t('me.delegations.pending.empty')}
              </p>
            ) : (
              <MyTable
                testId="my-delegations-pending"
                items={pending}
                renderActions={(item) => (
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`delegation-accept-${item.delegation.id}`}
                    onClick={() => {
                      setMessage(null);
                      setAccepting(item);
                    }}
                  >
                    {t('delegations.actions.accept')}
                  </Button>
                )}
              />
            )}
          </section>
          <section className="flex flex-col gap-2" aria-labelledby="my-delegations-current-title">
            <h2 id="my-delegations-current-title" className="text-title font-semibold">
              {t('me.delegations.current.title')}
            </h2>
            {others.length === 0 ? (
              <p
                className="text-body text-muted-foreground"
                data-testid="my-delegations-current-empty"
              >
                {t('me.delegations.current.empty')}
              </p>
            ) : (
              <MyTable testId="my-delegations-current" items={others} />
            )}
          </section>
        </>
      )}
      {accepting && (
        <AcceptDelegationDialog
          open
          delegation={accepting.delegation}
          onOpenChange={(open) => {
            if (!open) {
              setAccepting(null);
            }
          }}
          onDone={setMessage}
        />
      )}
    </section>
  );
}

function MyTable({
  items,
  testId,
  renderActions,
}: {
  readonly items: readonly MyDelegation[];
  readonly testId: string;
  readonly renderActions?: ((item: MyDelegation) => ReactNode) | undefined;
}) {
  const { t } = useTranslation('authz');
  const civil = useCivilDate();
  const taskName = useTaskName();
  const functionName = useFunctionName();
  return (
    <Table data-testid={testId} regionLabel={t('me.delegations.title')}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('me.delegations.columns.study')}</TableHead>
          <TableHead>{t('me.delegations.columns.function')}</TableHead>
          <TableHead>{t('me.delegations.columns.tasks')}</TableHead>
          <TableHead>{t('me.delegations.columns.validity')}</TableHead>
          <TableHead>{t('me.delegations.columns.status')}</TableHead>
          <TableHead>{t('me.delegations.columns.signatures')}</TableHead>
          {renderActions && (
            <TableHead>
              <span className="sr-only">{t('me.delegations.columns.actions')}</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.delegation.id} data-testid={`my-delegation-${item.delegation.id}`}>
            <TableCell>
              <span className="font-medium">
                {item.protocolCode ??
                  t('delegations.list.studyLabel', { id: shortId(item.delegation.studySiteId) })}
              </span>
              {item.studyTitle && (
                <span className="block text-caption text-muted-foreground">{item.studyTitle}</span>
              )}
            </TableCell>
            <TableCell>{functionName(item.delegation.functionCode)}</TableCell>
            <TableCell>
              <ul className="flex flex-col text-caption">
                {item.delegation.tasks.map((task) => (
                  <li key={task.taskCode}>{taskName(task.taskCode, task.name)}</li>
                ))}
              </ul>
            </TableCell>
            <TableCell>
              {t('delegations.validity', {
                from: civil(item.delegation.effectiveFrom),
                to: item.delegation.effectiveTo
                  ? civil(item.delegation.effectiveTo)
                  : t('delegations.openEnded'),
              })}
            </TableCell>
            <TableCell>
              <DelegationStatusBadge
                delegation={item.delegation}
                testId={`my-status-${item.delegation.id}`}
              />
              <DelegationReasonText reason={item.delegation.statusReason} />
            </TableCell>
            <TableCell>
              <DelegationSignatures delegation={item.delegation} />
            </TableCell>
            {renderActions && <TableCell>{renderActions(item)}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
